/**
 * The reseller's own commission plans (TMMAPI-0104, MM-D-106), reseller-wide on
 * `/v1/reseller-admin/commission-plans`: what its affiliates earn for the customers they bring. An
 * affiliate is given a plan on its agreement (TMMRSL-0050). Each normalizer reads the vendored
 * contract's generated schema and returns the console's own view, whose keys are always present.
 * Writes send the generated `CommissionPlanWriteRequest`, where an open-ended phase omits `toMonth`
 * (never `null`). Any role may GET and preview. The generated contract includes the bounty, milestone bonuses and caps.
 */
import { request } from './client';
import type {
  CommissionPlanMilestone,
  CommissionPlanList as CommissionPlanList_Gen,
  CommissionPlanPreview as CommissionPlanPreview_Gen,
  CommissionPlanPreviewExample as CommissionPlanPreviewExample_Gen,
  CommissionPlanPreviewMonth as CommissionPlanPreviewMonth_Gen,
  CommissionPlanPreviewRequest,
  CommissionPlanView as CommissionPlanView_Gen,
  CommissionPlanWriteRequest,
  PhaseView as PhaseView_Gen,
} from './generated';
import { asOptionalString } from './resellerAdmin';

export type CommissionPlanStatus = 'DRAFT' | 'ACTIVE' | 'CLOSED';
export type CommissionPlanMode = 'SIMPLE' | 'ADVANCED';
export type CommissionPlanAppliesTo = 'BASE' | 'ALL';
export type CommissionPlanAfterEnd = 'STOP' | 'HONOR';

/** Months `fromMonth`–`toMonth` at `rateBp`; `toMonth: null` = onward (only the last phase). */
export interface CommissionPhase {
  fromMonth: number;
  toMonth: number | null;
  rateBp: number;
}

/** One of the reseller's commission plans (`CommissionPlanView`, TMMAPI-0104). */
export interface CommissionPlan {
  id: number;
  name: string;
  status: CommissionPlanStatus;
  mode: CommissionPlanMode;
  /** The adopted card's currency (TMMAPI-0154). */
  currency: string;
  appliesTo: CommissionPlanAppliesTo;
  coolOffDays: number;
  afterEnd: CommissionPlanAfterEnd;
  /** Sorted by `fromMonth`. */
  phases: CommissionPhase[];
  /** Flagged by the server: can't be given to more affiliates. Nothing sets it since TMMAPI-0154. */
  noncompliant: boolean;
  noncompliantSince: string | null;
  createdAt: string;
  activatedAt: string | null;
  closedAt: string | null;
  /** Draft version (TMMAPI-0112). Sent back on a DRAFT's PATCH. */
  version: number;
  editableFields: string[];
  /** The bounty, milestone bonuses and caps. A `null` amount, payment number, or milestone list is unset. */
  extras: PlanExtras;
}

/** A milestone bonus: `amountMinor`, paid once when the affiliate reaches `qualifiedReferrals`. */
export type PlanMilestone = Required<CommissionPlanMilestone>;

/**
 * An ADVANCED plan's extras (MM-D-106). `bountyOnPayment` and `milestones` are required nullables:
 * `null` passes through. The editor's default payment of 1 and empty milestone rows live in `planToForm` only.
 */
export type PlanExtras = Pick<
  CommissionPlanView_Gen,
  | 'bountyMinor'
  | 'bountyOnPayment'
  | 'milestones'
  | 'perReferralCapMinor'
  | 'perAffiliateMonthCapMinor'
>;

/** The extras on a write body: sent only when the plan read carried them. */
export type PlanExtrasWrite = Pick<
  CommissionPlanWriteRequest,
  | 'bountyMinor'
  | 'bountyOnPayment'
  | 'milestones'
  | 'perReferralCapMinor'
  | 'perAffiliateMonthCapMinor'
>;

/** Generated write and preview bodies include bonuses, caps and draft versions. */
export type CommissionPlanWrite = CommissionPlanWriteRequest;
export type CommissionPlanPreviewBody = CommissionPlanPreviewRequest;

/** One month of the 24-month example (month 1 = the referral's first payment; an annual payment counts as 12). */
export interface PlanExampleMonth {
  month: number;
  /** The day this month's payment is collected (`YYYY-MM-DD`, a month apart from today, UTC). */
  collectedOn: string;
  rateBp: number;
  commissionMinor: number;
  marginBeforeMinor: number;
  marginAfterMinor: number;
  /** The day this month's commission becomes payable (`YYYY-MM-DD`). */
  payableOn: string;
  /** The bounty earned this month; `null` except in the bounty's month (TMMAPI-0105). */
  bountyMinor: number | null;
  /** A cap held this month's earnings back (TMMAPI-0105). */
  capped: boolean;
}

/**
 * One subscriber at the card's monthly base (`retailMinor`, the same every month), paying from
 * month 1; months sorted by `month`.
 */
export interface PlanExample {
  currency: string;
  rateCardId: number;
  retailMinor: number;
  coolOffDays: number;
  months: PlanExampleMonth[];
  /** A month's `bountyMinor` is non-null: the example has a Bounty column. */
  hasBounty: boolean;
}

/** `POST /commission-plans/preview`: the limit check and the 24-month example; writes nothing. */
export interface PlanPreview {
  valid: boolean;
  /** Write field → the server's limit message; keys may carry `[i]` / `.x` (`phases[1].rateBp`). */
  fields: Record<string, string>;
  /** `null` while the schedule itself is invalid. */
  example: PlanExample | null;
}

const STATUSES: readonly CommissionPlanStatus[] = ['DRAFT', 'ACTIVE', 'CLOSED'];
const MODES: readonly CommissionPlanMode[] = ['SIMPLE', 'ADVANCED'];

function normalizePhases(phases: PhaseView_Gen[]): CommissionPhase[] {
  return phases
    .map((phase) => ({
      fromMonth: phase.fromMonth,
      // An absent `toMonth` is an open-ended phase.
      toMonth: phase.toMonth ?? null,
      rateBp: phase.rateBp,
    }))
    .sort((a, b) => a.fromMonth - b.fromMonth);
}

/** Milestones as sent, sorted by `qualifiedReferrals`. `null` (unset) stays `null` — `[]` on a write removes the list. */
function normalizeMilestones(
  value: CommissionPlanMilestone[] | null,
): CommissionPlanMilestone[] | null {
  if (value === null) return null;
  return [...value].sort((a, b) => a.qualifiedReferrals - b.qualifiedReferrals);
}

/** Extras as sent. A null payment number and a null milestone list pass through. */
function normalizeExtras(raw: CommissionPlanView_Gen): PlanExtras {
  return {
    bountyMinor: raw.bountyMinor,
    bountyOnPayment: raw.bountyOnPayment,
    milestones: normalizeMilestones(raw.milestones),
    perReferralCapMinor: raw.perReferralCapMinor,
    perAffiliateMonthCapMinor: raw.perAffiliateMonthCapMinor,
  };
}

function normalizePlan(body: unknown): CommissionPlan {
  const raw = body as CommissionPlanView_Gen;
  return {
    id: raw.id,
    name: raw.name,
    editableFields: raw.editableFields,
    // An unknown status is neither editable nor assignable.
    status: STATUSES.find((status) => status === raw.status) ?? 'CLOSED',
    // An unknown mode shows every phase.
    mode: MODES.find((mode) => mode === raw.mode) ?? 'ADVANCED',
    currency: raw.currency,
    appliesTo: raw.appliesTo === 'ALL' ? 'ALL' : 'BASE',
    coolOffDays: raw.coolOffDays,
    afterEnd: raw.afterEnd === 'HONOR' ? 'HONOR' : 'STOP',
    phases: normalizePhases(raw.phases),
    noncompliant: raw.noncompliant,
    noncompliantSince: asOptionalString(raw.noncompliantSince) ?? null,
    createdAt: raw.createdAt,
    activatedAt: asOptionalString(raw.activatedAt) ?? null,
    closedAt: asOptionalString(raw.closedAt) ?? null,
    version: raw.version,
    extras: normalizeExtras(raw),
  };
}

function normalizePlanList(body: unknown): CommissionPlan[] {
  const raw = body as CommissionPlanList_Gen;
  return raw.commissionPlans.map(normalizePlan);
}

function normalizeExampleMonths(months: CommissionPlanPreviewMonth_Gen[]): PlanExampleMonth[] {
  return months
    .map((month) => ({
      month: month.month,
      collectedOn: month.collectedOn,
      rateBp: month.rateBp,
      commissionMinor: month.commissionMinor,
      marginBeforeMinor: month.marginBeforeMinor,
      marginAfterMinor: month.marginAfterMinor,
      payableOn: month.payableOn,
      bountyMinor: month.bountyMinor,
      capped: month.capped,
    }))
    .sort((a, b) => a.month - b.month);
}

function normalizeExample(raw: CommissionPlanPreviewExample_Gen | undefined): PlanExample | null {
  if (raw === undefined) return null;
  return {
    currency: raw.currency,
    rateCardId: raw.rateCardId,
    retailMinor: raw.retailMinor,
    coolOffDays: raw.coolOffDays,
    months: normalizeExampleMonths(raw.months),
    hasBounty: raw.months.some((month) => month.bountyMinor !== null),
  };
}

function normalizePreview(body: unknown): PlanPreview {
  const raw = body as CommissionPlanPreview_Gen;
  return { valid: raw.valid, fields: { ...raw.fields }, example: normalizeExample(raw.example) };
}

const COMMISSION_PLANS = '/v1/reseller-admin/commission-plans';

/** Writes need OWNER or ADMIN (`403 RESELLER_ROLE_FORBIDDEN`); only a DRAFT may be patched. */
export const commissionPlansApi = {
  /** Every plan the reseller owns, in any status. */
  async listPlans(): Promise<CommissionPlan[]> {
    return normalizePlanList(await request<unknown>('GET', COMMISSION_PLANS));
  },

  /** One plan (`404 COMMISSION_PLAN_NOT_FOUND` when it isn't the reseller's). */
  async getPlan(id: number): Promise<CommissionPlan> {
    return normalizePlan(await request<unknown>('GET', `${COMMISSION_PLANS}/${id}`));
  },

  /** A new SIMPLE draft: 10% for 12 months, BASE, cool-off 30, STOP (`201`). No body. */
  async createPlan(): Promise<CommissionPlan> {
    return normalizePlan(await request<unknown>('POST', COMMISSION_PLANS));
  },

  /** A draft's changes (`409 COMMISSION_PLAN_IMMUTABLE` once it isn't a draft). */
  async patchPlan(id: number, body: CommissionPlanWrite): Promise<CommissionPlan> {
    return normalizePlan(
      await request<unknown>('PATCH', `${COMMISSION_PLANS}/${id}`, { json: body }),
    );
  },

  /**
   * DRAFT → ACTIVE (frozen from then on; `400 VALIDATION_FAILED` when out of limits,
   * `409 COMMISSION_PLAN_IMMUTABLE` for a CLOSED plan).
   */
  async activatePlan(id: number): Promise<CommissionPlan> {
    return normalizePlan(await request<unknown>('POST', `${COMMISSION_PLANS}/${id}/activate`));
  },

  /** No more affiliates; those already on it keep it. */
  async closePlan(id: number): Promise<CommissionPlan> {
    return normalizePlan(await request<unknown>('POST', `${COMMISSION_PLANS}/${id}/close`));
  },

  /** A new DRAFT with the plan's values (`201`). */
  async copyPlan(id: number): Promise<CommissionPlan> {
    return normalizePlan(await request<unknown>('POST', `${COMMISSION_PLANS}/${id}/copy`));
  },

  /**
   * The dry run on one of the reseller's cards: any role, writes nothing. `404 RATE_CARD_NOT_FOUND`
   * when the card isn't the reseller's; a missing card is `fields.rateCardId`.
   */
  async previewPlan(body: CommissionPlanPreviewBody): Promise<PlanPreview> {
    return normalizePreview(
      await request<unknown>('POST', `${COMMISSION_PLANS}/preview`, { json: body }),
    );
  },
};
